import { CARD_COLOUR_HEX, CARD_COLOURS, LIMITS, type CardColour } from './constants.js'
import { CONTEXT_LIMITS, newRelationship, type Relationship } from './context.js'
import { DomainError } from './errors.js'
import { newId, nowIso } from './ids.js'
import { emptyCardIntent, INTENT_LIMITS } from './intent.js'
import { MINDMAP_ROOT_COLOUR, effectiveColour, findMindmapNode, mindmapNodeIds, type MindmapNode } from './mindmap.js'
import { cellKey, type NormalizedBoard } from './normalized.js'
import type { CardPatch, Operation } from './operations.js'
import {
  emptyPlacement,
  type BoardSnapshot,
  type Card,
  type Column,
  type ColumnGroup,
  type LinkSync,
  type NodeCore,
  type Placement,
  type Row,
  type StorymapLayout,
  type Swimlane,
} from './schemas.js'
import type { CommandResult } from './commands.js'

/**
 * Turning a mindmap branch into a story map, by depth from the selected node. The
 * `columns` layout (the default in the dialog):
 *
 *   depth 0      → the board name
 *   depth 1      → columns, ungrouped; the user adds groups by hand
 *   depth 2, 3 … → cards in swimlane "Level 2", "Level 3" …, in their depth-1 ancestor's column
 *
 * The `groups` layout (every board generated before the choice existed):
 *
 *   depth 0      → the board name
 *   depth 1      → column groups (activities)
 *   depth 2      → columns (steps), in their parent's group
 *   depth 3, 4 … → cards in swimlane "Level 3", "Level 4" …, in their depth-2 ancestor's column
 *
 * Each swimlane has one row, titled "Todo: Rename". A card below the first card level is
 * linked from its parent's card with a `contains` relationship, so the exact hierarchy
 * survives the flattening. Every generated entity records the node it came from
 * (`sourceNodeId`); the board records the branch and the layout.
 */

export const GENERATED_ROW_TITLE = 'Todo: Rename'
export const levelTitle = (depth: number) => `Level ${depth}`

const rgb = (hex: string) => [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16)) as [number, number, number]

/** The nearest card colour to a `#rrggbb` value; `default` when there is none. */
export function nearestCardColour(hex: string | null): CardColour {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return 'default'
  const [r, g, b] = rgb(hex)
  let best: CardColour = 'default'
  let bestDistance = Infinity
  for (const colour of CARD_COLOURS) {
    if (colour === 'default') continue
    const [cr, cg, cb] = rgb(CARD_COLOUR_HEX[colour])
    // Weighted for perceived difference (the "redmean" approximation).
    const mean = (r + cr) / 2
    const distance = (2 + mean / 256) * (r - cr) ** 2 + 4 * (g - cg) ** 2 + (2 + (255 - mean) / 256) * (b - cb) ** 2
    if (distance < bestDistance) {
      best = colour
      bestDistance = distance
    }
  }
  return best
}

const cut = (text: string, max: number) => (text.length > max ? text.slice(0, max) : text)

/** Text too long for a title is cut, and kept whole at the top of the description. */
function titleAndOverflow(text: string, max: number): { title: string; overflow: string } {
  return text.length > max ? { title: cut(text, max), overflow: text } : { title: text, overflow: '' }
}

function joinNote(overflow: string, note: string): string {
  return cut([overflow, note].filter(Boolean).join('\n\n'), LIMITS.description)
}

// --- placement: where a node's card lands, named by titles --------------------------

const sameTitle = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** Equal ignoring case and surrounding space; absent is none set. */
export function samePlacement(a: Placement | undefined, b: Placement | undefined): boolean {
  const x = a ?? emptyPlacement()
  const y = b ?? emptyPlacement()
  return sameTitle(x.group, y.group) && sameTitle(x.column, y.column) && sameTitle(x.swimlane, y.swimlane) && sameTitle(x.row, y.row)
}

interface Structure {
  groups: { id: string; title: string }[]
  columns: { id: string; title: string; groupId: string | null }[]
  swimlanes: { id: string; title: string }[]
  /** In board order within each swimlane. */
  rows: { id: string; title: string; swimlaneId: string }[]
}

/**
 * Finds the group, column, swimlane and row a placement names, matching titles ignoring
 * case, and makes what is missing. What it makes is kept in `made`, in order, for the
 * caller to add to a new board or to send as operations to an existing one.
 */
export class PlacementResolver {
  private readonly known: Structure
  readonly made: Structure = { groups: [], columns: [], swimlanes: [], rows: [] }

  constructor(known: Structure) {
    this.known = { groups: [...known.groups], columns: [...known.columns], swimlanes: [...known.swimlanes], rows: [...known.rows] }
  }

  static fromBoard(state: NormalizedBoard): PlacementResolver {
    return new PlacementResolver({
      groups: Object.values(state.groups).map(({ id, title }) => ({ id, title })),
      columns: state.columnIds.map((id) => ({ id, title: state.columns[id]!.title, groupId: state.columns[id]!.groupId })),
      swimlanes: state.swimlaneIds.map((id) => ({ id, title: state.swimlanes[id]!.title })),
      rows: state.swimlaneIds.flatMap((swimlaneId) =>
        (state.rowIdsBySwimlane[swimlaneId] ?? []).map((id) => ({ id, title: state.rows[id]!.title, swimlaneId })),
      ),
    })
  }

  /** Something made by other means while planning, so a placement can find it. */
  remember(kind: 'groups' | 'columns' | 'swimlanes' | 'rows', entity: Structure[typeof kind][number]): void {
    ;(this.known[kind] as (typeof entity)[]).push(entity)
  }

  private group(title: string): string {
    const found = this.known.groups.find((group) => sameTitle(group.title, title))
    if (found) return found.id
    const group = { id: newId(), title: title.trim() }
    this.known.groups.push(group)
    this.made.groups.push(group)
    return group.id
  }

  /** A column named by title; a new one goes into `groupTitle`'s group, when one is named. */
  column(title: string, groupTitle: string): string {
    const found = this.known.columns.find((column) => sameTitle(column.title, title))
    if (found) return found.id
    const column = { id: newId(), title: title.trim(), groupId: groupTitle.trim() ? this.group(groupTitle) : null }
    this.known.columns.push(column)
    this.made.columns.push(column)
    return column.id
  }

  swimlane(title: string): string {
    const found = this.known.swimlanes.find((swimlane) => sameTitle(swimlane.title, title))
    if (found) return found.id
    const swimlane = { id: newId(), title: title.trim() }
    this.known.swimlanes.push(swimlane)
    this.made.swimlanes.push(swimlane)
    return swimlane.id
  }

  /** A row named by title within a swimlane. */
  row(swimlaneId: string, title: string): string {
    const found = this.known.rows.find((row) => row.swimlaneId === swimlaneId && sameTitle(row.title, title))
    if (found) return found.id
    const row = { id: newId(), title: title.trim(), swimlaneId }
    this.known.rows.push(row)
    this.made.rows.push(row)
    return row.id
  }

  /** A swimlane's first row, made (as a generated row) when it has none. */
  firstRow(swimlaneId: string): string {
    return this.known.rows.find((row) => row.swimlaneId === swimlaneId)?.id ?? this.row(swimlaneId, GENERATED_ROW_TITLE)
  }

  swimlaneOf(rowId: string): string {
    return this.known.rows.find((row) => row.id === rowId)!.swimlaneId
  }

  /** What was made, as operations on an existing board, and their undo. */
  operations(boardId: string): { forward: Operation[]; inverse: Operation[] } {
    const forward: Operation[] = []
    const inverse: Operation[] = []
    const madeLanes = new Set(this.made.swimlanes.map((swimlane) => swimlane.id))
    for (const group of this.made.groups) {
      forward.push({ type: 'group.create', group: { ...group, boardId } })
      inverse.unshift({ type: 'group.delete', id: group.id })
    }
    for (const column of this.made.columns) {
      forward.push({ type: 'column.create', column: { ...column, boardId }, beforeId: null })
      inverse.unshift({ type: 'column.delete', id: column.id })
    }
    for (const swimlane of this.made.swimlanes) {
      forward.push({ type: 'swimlane.create', swimlane: { ...swimlane, boardId }, beforeId: null })
      inverse.unshift({ type: 'swimlane.delete', id: swimlane.id })
    }
    for (const row of this.made.rows) {
      forward.push({ type: 'row.create', row: { ...row, boardId }, beforeId: null })
      // A row in a new swimlane goes with it.
      if (!madeLanes.has(row.swimlaneId)) inverse.unshift({ type: 'row.delete', id: row.id })
    }
    return { forward, inverse }
  }
}

/**
 * The cell for a node's card. Each placement field that is set overrides; the rest come
 * from `defaults`. A row without a swimlane is looked for in the default row's swimlane;
 * a swimlane without a row uses its first row. A group applies to a column it makes.
 */
export function placeCard(
  resolver: PlacementResolver,
  placement: Placement | undefined,
  defaults: { columnId: () => string | undefined; rowId: () => string | undefined },
): { columnId: string | undefined; rowId: string | undefined } {
  const p = placement ?? emptyPlacement()
  const columnId = p.column.trim() ? resolver.column(p.column, p.group) : defaults.columnId()
  if (p.swimlane.trim()) {
    const swimlaneId = resolver.swimlane(p.swimlane)
    return { columnId, rowId: p.row.trim() ? resolver.row(swimlaneId, p.row) : resolver.firstRow(swimlaneId) }
  }
  const fallback = defaults.rowId()
  if (p.row.trim() && fallback) return { columnId, rowId: resolver.row(resolver.swimlaneOf(fallback), p.row) }
  return { columnId, rowId: fallback }
}

/** Card order per cell as a command's moves change it, so each move's undo puts the card back in place. */
export class CellOrder {
  private readonly cells = new Map<string, string[]>()

  constructor(private readonly state: NormalizedBoard) {}

  private ids(rowId: string, columnId: string): string[] {
    const key = cellKey(rowId, columnId)
    let ids = this.cells.get(key)
    if (!ids) {
      ids = [...(this.state.cardIdsByCell[key] ?? [])]
      this.cells.set(key, ids)
    }
    return ids
  }

  /** Moves the card to the end of its new cell; returns the card it stood before in the old one. */
  move(cardId: string, from: { rowId: string; columnId: string }, to: { rowId: string; columnId: string }): string | null {
    const source = this.ids(from.rowId, from.columnId)
    const index = source.indexOf(cardId)
    const next = index >= 0 ? (source[index + 1] ?? null) : null
    if (index >= 0) source.splice(index, 1)
    this.ids(to.rowId, to.columnId).push(cardId)
    return next
  }
}

/**
 * Brings a card generated from a node up to date with it: the fields that differ, and its
 * place, but only when the node's placement differs from the one the card was placed by —
 * a card moved by hand stays where it was put until then.
 */
export function updateMatchedCard(
  order: CellOrder,
  card: Card,
  fields: CardPatch,
  placement: Placement | undefined,
  place: () => { columnId: string | undefined; rowId: string | undefined },
): { forward: Operation[]; inverse: Operation[] } {
  const patch: Record<string, unknown> = {}
  const before: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    const current = (card as Record<string, unknown>)[key]
    if (value === undefined || JSON.stringify(value) === JSON.stringify(current)) continue
    patch[key] = value
    before[key] = current
  }
  const forward: Operation[] = []
  const inverse: Operation[] = []
  if (!samePlacement(card.sourcePlacement, placement)) {
    const target = place()
    if (target.columnId && target.rowId && (target.columnId !== card.columnId || target.rowId !== card.rowId)) {
      const next = order.move(card.id, card, { rowId: target.rowId, columnId: target.columnId })
      forward.push({ type: 'card.move', id: card.id, rowId: target.rowId, columnId: target.columnId, beforeId: null })
      inverse.unshift({ type: 'card.move', id: card.id, rowId: card.rowId, columnId: card.columnId, beforeId: next })
    }
    patch.sourcePlacement = placement ?? emptyPlacement()
    before.sourcePlacement = card.sourcePlacement ?? emptyPlacement()
  }
  if (Object.keys(patch).length > 0) {
    forward.unshift({ type: 'card.update', id: card.id, patch: patch as CardPatch })
    inverse.push({ type: 'card.update', id: card.id, patch: before as CardPatch })
  }
  return { forward, inverse }
}

export interface GeneratedPlan {
  groups: Omit<ColumnGroup, 'boardId'>[]
  columns: Omit<Column, 'boardId' | 'position'>[]
  /** By depth, top to bottom. */
  levels: number[]
  cards: GeneratedCard[]
  warnings: string[]
}

export interface GeneratedCard {
  node: MindmapNode
  depth: number
  /** The source node of the column the card sits in. */
  columnNodeId: string
  /** The node of the card that contains this one (below the first card level). */
  parentNodeId: string | null
  /** The colour the node is drawn with: its own, or inherited from its branch. */
  colour: string
}

/**
 * The layout a branch maps to, before any board ids are given out. Shared by generation
 * and by updating an existing board, so the two cannot place things differently.
 */
export function planBranch(root: MindmapNode, nodeId: string, layout: StorymapLayout = 'groups'): GeneratedPlan & { branch: MindmapNode } {
  const branch = findMindmapNode(root, nodeId)
  if (!branch) throw new DomainError('not_found', 'That node is no longer in the mindmap.')
  const groups: GeneratedPlan['groups'] = []
  const columns: GeneratedPlan['columns'] = []
  const cards: GeneratedCard[] = []
  const levels = new Set<number>()

  const branchColour = effectiveColour(root, branch.id)
  const firstCardDepth = layout === 'columns' ? 2 : 3
  // Every node below a column's node is a card; one deeper than the first is contained by its parent.
  const cardsUnder = (step: MindmapNode, inherited: string) => {
    const walk = (current: MindmapNode, depth: number, parent: MindmapNode | null, inherited: string) => {
      const colour = current.colour ?? inherited
      levels.add(depth)
      cards.push({ node: current, depth, columnNodeId: step.id, parentNodeId: depth > firstCardDepth && parent ? parent.id : null, colour })
      for (const child of current.children) walk(child, depth + 1, current, colour)
    }
    for (const child of step.children) walk(child, firstCardDepth, null, inherited)
  }
  if (layout === 'columns') {
    for (const step of branch.children) {
      const column = titleAndOverflow(step.title, LIMITS.title)
      columns.push({ id: newId(), title: column.title, groupId: null, notes: joinNote(column.overflow, step.description), sourceNodeId: step.id })
      cardsUnder(step, step.colour ?? branchColour)
    }
  }
  for (const activity of layout === 'groups' ? branch.children : []) {
    const activityColour = activity.colour ?? branchColour
    const group = titleAndOverflow(activity.title, LIMITS.title)
    const groupId = newId()
    groups.push({ id: groupId, title: group.title, notes: joinNote(group.overflow, activity.description), sourceNodeId: activity.id })
    // An activity with no steps still shows on the map: one column of the same name.
    const steps = activity.children.length > 0 ? activity.children : [null]
    for (const step of steps) {
      const node = step ?? activity
      const column = titleAndOverflow(node.title, LIMITS.title)
      columns.push({
        id: newId(),
        title: column.title,
        groupId,
        notes: step ? joinNote(column.overflow, step.description) : '',
        sourceNodeId: node.id,
      })
      if (!step) continue
      cardsUnder(step, step.colour ?? activityColour)
    }
  }

  const warnings: string[] = []
  if (branch.children.length === 0) warnings.push('This node has no children, so the storymap will be empty.')
  if (cards.length > LIMITS.importCards) {
    throw new DomainError('validation', `This branch would make ${cards.length} cards; a board holds at most ${LIMITS.importCards}.`)
  }
  const children = new Map<string, number>()
  for (const card of cards) if (card.parentNodeId) children.set(card.parentNodeId, (children.get(card.parentNodeId) ?? 0) + 1)
  for (const [parentId, count] of children) {
    if (count > CONTEXT_LIMITS.relationshipCount) {
      const parent = findMindmapNode(branch, parentId)
      throw new DomainError(
        'validation',
        `"${parent?.title ?? parentId}" has ${count} children; a card can contain at most ${CONTEXT_LIMITS.relationshipCount}.`,
      )
    }
  }
  // Somewhere to put cards, even when the branch has none yet.
  if (levels.size === 0) levels.add(firstCardDepth)
  return { branch, groups, columns, levels: [...levels].sort((a, b) => a - b), cards, warnings }
}

/** The depths that need a "Level n" swimlane: those with a card not given a swimlane of its own. */
function defaultLevels(plan: GeneratedPlan): number[] {
  if (plan.cards.length === 0) return plan.levels
  return plan.levels.filter((depth) => plan.cards.some((card) => card.depth === depth && !card.node.placement?.swimlane?.trim()))
}

// --- what a node carries to its card ---------------------------------------------

/**
 * A node's shared fields as they go onto its card on an update: title, description and
 * tags always; type, status, owner and icon when the node has one, so a card's own value
 * stands until its node is given one.
 */
export function nodeCardFields(node: NodeCore): CardPatch {
  return {
    title: node.title,
    description: node.description,
    tags: node.tags,
    ...(node.type ? { type: node.type } : {}),
    ...(node.status ? { status: node.status } : {}),
    ...(node.owner ? { owner: node.owner } : {}),
    ...(node.icon ? { icon: node.icon } : {}),
  }
}

/** A new card for a node, with every shared field; relationships are added once every card has an id. */
export function cardFromNode(boardId: string, node: NodeCore, cell: { rowId: string; columnId: string }, colour: CardColour, now: string): Card {
  return {
    id: newId(),
    boardId,
    rowId: cell.rowId,
    columnId: cell.columnId,
    position: 0,
    title: node.title,
    description: node.description,
    type: node.type,
    status: node.status ?? 'new',
    priority: null,
    moscow: null,
    owner: node.owner,
    tags: node.tags,
    colour,
    icon: node.icon,
    estimateMinutes: null,
    dueDate: null,
    release: '',
    intent: { ...emptyCardIntent(), references: node.references },
    relationships: [],
    sourceNodeId: node.id,
    sourcePlacement: node.placement,
    createdAt: now,
    updatedAt: now,
  }
}

/** A node's relationships pointed at the cards made from their targets; one to a node with no card is dropped. */
export function cardRelationships(node: NodeCore, cardIdByNode: ReadonlyMap<string, string>): Relationship[] {
  return node.relationships.flatMap((relationship) => {
    const targetId = cardIdByNode.get(relationship.targetId)
    return targetId ? [{ ...relationship, id: newId(), targetId }] : []
  })
}

const sameLink = (a: Relationship, b: Relationship) => a.type === b.type && a.targetId === b.targetId

/**
 * The relationships (`extra`, then the node's own) and references a card generated from a
 * node lacks, as one patch and its undo; null when it has them all. Links are only added,
 * never removed, so those made by hand on the board stay.
 */
export function linksToAdd(
  card: Card,
  node: NodeCore,
  cardIdByNode: ReadonlyMap<string, string>,
  extra: readonly Relationship[] = [],
): { patch: CardPatch; before: CardPatch } | null {
  const current = card.relationships ?? []
  const relationships: Relationship[] = []
  for (const link of [...extra, ...cardRelationships(node, cardIdByNode)]) {
    if (!current.some((existing) => sameLink(existing, link)) && !relationships.some((added) => sameLink(added, link))) relationships.push(link)
  }
  const references = node.references.filter(
    (reference) => !card.intent.references.some((existing) => existing.locator === reference.locator && existing.label === reference.label),
  )
  const patch: CardPatch = {}
  const before: CardPatch = {}
  if (relationships.length > 0) {
    patch.relationships = [...current, ...relationships].slice(0, CONTEXT_LIMITS.relationshipCount)
    before.relationships = current
  }
  if (references.length > 0) {
    patch.intent = { ...card.intent, references: [...card.intent.references, ...references].slice(0, INTENT_LIMITS.referenceCount) }
    before.intent = card.intent
  }
  return Object.keys(patch).length > 0 ? { patch, before } : null
}

function newCard(boardId: string, generated: GeneratedCard, rowId: string, columnId: string, now: string): Card {
  return cardFromNode(boardId, generated.node, { rowId, columnId }, nearestCardColour(generated.colour), now)
}

/** A complete new board for a branch, ready for `POST /api/boards/import`. */
export function mindmapBranchToSnapshot(input: {
  mindmapId: string
  root: MindmapNode
  nodeId: string
  name: string
  layout?: StorymapLayout
  boardId?: string
  now?: string
}): { snapshot: BoardSnapshot; warnings: string[] } {
  const now = input.now ?? nowIso()
  const boardId = input.boardId ?? newId()
  const layout = input.layout ?? 'groups'
  const plan = planBranch(input.root, input.nodeId, layout)

  const columnGroups: ColumnGroup[] = plan.groups.map((group) => ({ ...group, boardId }))
  const columns: Column[] = plan.columns.map((column, position) => ({ ...column, boardId, position }))
  if (columns.length === 0) {
    columns.push({ id: newId(), boardId, title: 'New column', groupId: null, position: 0 })
  }
  const swimlanes: Swimlane[] = []
  const rows: Row[] = []
  const rowByDepth = new Map<number, string>()
  for (const [position, depth] of defaultLevels(plan).entries()) {
    const swimlane: Swimlane = { id: newId(), boardId, title: levelTitle(depth), position }
    const row: Row = { id: newId(), boardId, swimlaneId: swimlane.id, title: GENERATED_ROW_TITLE, position: 0 }
    swimlanes.push(swimlane)
    rows.push(row)
    rowByDepth.set(depth, row.id)
  }

  const columnByNode = new Map(plan.columns.map((column) => [column.sourceNodeId!, column.id]))
  const cardByNode = new Map<string, Card>()
  const cards: Card[] = []
  // A node's placement, where set, overrides the depth layout; what it names is made as needed.
  const resolver = new PlacementResolver({ groups: columnGroups, columns, swimlanes, rows })
  for (const generated of plan.cards) {
    const target = placeCard(resolver, generated.node.placement, {
      columnId: () => columnByNode.get(generated.columnNodeId),
      rowId: () => rowByDepth.get(generated.depth),
    })
    const card = newCard(boardId, generated, target.rowId!, target.columnId!, now)
    cardByNode.set(generated.node.id, card)
    cards.push(card)
  }
  // The nodes' own relationships, now every card has an id.
  const cardIdByNode = new Map([...cardByNode].map(([nodeId, card]) => [nodeId, card.id]))
  for (const generated of plan.cards) cardByNode.get(generated.node.id)!.relationships = cardRelationships(generated.node, cardIdByNode)
  for (const group of resolver.made.groups) columnGroups.push({ ...group, boardId })
  for (const column of resolver.made.columns) columns.push({ ...column, boardId, position: columns.length })
  for (const swimlane of resolver.made.swimlanes) swimlanes.push({ ...swimlane, boardId, position: swimlanes.length })
  for (const row of resolver.made.rows) {
    rows.push({ ...row, boardId, position: rows.filter((other) => other.swimlaneId === row.swimlaneId).length })
  }
  for (const generated of plan.cards) {
    if (!generated.parentNodeId) continue
    const parent = cardByNode.get(generated.parentNodeId)!
    parent.relationships = [...(parent.relationships ?? []), newRelationship(cardByNode.get(generated.node.id)!.id, 'contains')].slice(
      0,
      CONTEXT_LIMITS.relationshipCount,
    )
  }
  // Pre-order already lines children up under their parents within each cell.
  const position = new Map<string, number>()
  for (const card of cards) {
    const cell = `${card.rowId}|${card.columnId}`
    card.position = position.get(cell) ?? 0
    position.set(cell, card.position + 1)
  }

  const branch = plan.branch
  const name = cut((input.name.trim() || branch.title).trim(), LIMITS.title)
  return {
    snapshot: {
      board: {
        id: boardId,
        name,
        revision: 0,
        createdAt: now,
        updatedAt: now,
        source: { mindmapId: input.mindmapId, nodeId: branch.id, generatedAt: now, layout },
      },
      columnGroups,
      columns,
      swimlanes,
      rows,
      cards,
    },
    warnings: plan.warnings,
  }
}

export interface UpdateSummary {
  updated: number
  added: number
  /** Generated entities whose node has gone from the branch. Never deleted. */
  sourceRemoved: number
}

/**
 * Brings a board generated from a branch up to date with it, as one undoable command.
 * Matching is by `sourceNodeId`. Matched entities take the node's text, note, status and
 * colour, and keep their place. New nodes are added where the mapping would put them.
 * Entities whose node is gone, and everything added by hand, are left alone.
 */
export function planStorymapUpdate(state: NormalizedBoard, root: MindmapNode, nodeId: string): { result: CommandResult | null; summary: UpdateSummary } {
  // The layout the board was generated with, so an update never re-maps it.
  const plan = planBranch(root, nodeId, state.board.source?.layout ?? 'groups')
  const boardId = state.board.id
  const now = nowIso()
  const forward: Operation[] = []
  const inverse: Operation[] = []
  const summary: UpdateSummary = { updated: 0, added: 0, sourceRemoved: 0 }
  const live = new Set(mindmapNodeIds(plan.branch))

  const bySource = <T extends { sourceNodeId?: string | undefined }>(items: Record<string, T>) => {
    const map = new Map<string, T>()
    for (const item of Object.values(items)) if (item.sourceNodeId && !map.has(item.sourceNodeId)) map.set(item.sourceNodeId, item)
    return map
  }
  const groups = bySource(state.groups)
  const columns = bySource(state.columns)
  const cards = bySource(state.cards)
  // What node placements name, found on the board or made; told of what the layout makes below.
  const resolver = PlacementResolver.fromBoard(state)

  // --- groups and columns ---
  const groupIdByNode = new Map<string, string>()
  for (const group of plan.groups) {
    const existing = groups.get(group.sourceNodeId!)
    if (!existing) {
      forward.push({ type: 'group.create', group: { ...group, boardId } })
      inverse.unshift({ type: 'group.delete', id: group.id })
      resolver.remember('groups', { id: group.id, title: group.title })
      groupIdByNode.set(group.sourceNodeId!, group.id)
      summary.added += 1
      continue
    }
    groupIdByNode.set(group.sourceNodeId!, existing.id)
    let changed = false
    if (existing.title !== group.title) {
      forward.push({ type: 'group.rename', id: existing.id, title: group.title })
      inverse.unshift({ type: 'group.rename', id: existing.id, title: existing.title })
      changed = true
    }
    if ((existing.notes ?? '') !== (group.notes ?? '')) {
      forward.push({ type: 'group.setNotes', id: existing.id, notes: group.notes ?? '' })
      inverse.unshift({ type: 'group.setNotes', id: existing.id, notes: existing.notes ?? '' })
      changed = true
    }
    if (changed) summary.updated += 1
  }

  const columnIdByNode = new Map<string, string>()
  for (const column of plan.columns) {
    // A one-column activity uses its own node for the column: told apart by kind of entity.
    const existing = columns.get(column.sourceNodeId!)
    if (!existing) {
      const groupNodeId = plan.groups.find((group) => group.id === column.groupId)?.sourceNodeId
      const groupId = groupNodeId ? (groupIdByNode.get(groupNodeId) ?? null) : null
      forward.push({ type: 'column.create', column: { ...column, boardId, groupId }, beforeId: null })
      inverse.unshift({ type: 'column.delete', id: column.id })
      resolver.remember('columns', { id: column.id, title: column.title, groupId })
      columnIdByNode.set(column.sourceNodeId!, column.id)
      summary.added += 1
      continue
    }
    columnIdByNode.set(column.sourceNodeId!, existing.id)
    let changed = false
    if (existing.title !== column.title) {
      forward.push({ type: 'column.rename', id: existing.id, title: column.title })
      inverse.unshift({ type: 'column.rename', id: existing.id, title: existing.title })
      changed = true
    }
    if ((existing.notes ?? '') !== (column.notes ?? '')) {
      forward.push({ type: 'column.setNotes', id: existing.id, notes: column.notes ?? '' })
      inverse.unshift({ type: 'column.setNotes', id: existing.id, notes: existing.notes ?? '' })
      changed = true
    }
    if (changed) summary.updated += 1
  }

  // --- a row for each depth: the swimlane already holding that depth's cards, else "Level n" ---
  const depthOf = new Map(plan.cards.map((card) => [card.node.id, card.depth]))
  const rowByDepth = new Map<number, string>()
  for (const card of Object.values(state.cards)) {
    // A card its node placed in a swimlane or row of its own says nothing about its depth's row.
    if (card.sourcePlacement?.swimlane.trim() || card.sourcePlacement?.row.trim()) continue
    const depth = card.sourceNodeId ? depthOf.get(card.sourceNodeId) : undefined
    if (depth !== undefined && !rowByDepth.has(depth) && state.rows[card.rowId]) rowByDepth.set(depth, card.rowId)
  }
  for (const depth of plan.levels) {
    if (rowByDepth.has(depth)) continue
    const swimlane = Object.values(state.swimlanes).find((lane) => lane.title === levelTitle(depth))
    const firstRow = swimlane ? state.rowIdsBySwimlane[swimlane.id]?.[0] : undefined
    if (firstRow) {
      rowByDepth.set(depth, firstRow)
      continue
    }
    if (!plan.cards.some((card) => card.depth === depth && !card.node.placement?.swimlane?.trim())) continue
    const swimlaneId = newId()
    const rowId = newId()
    forward.push(
      { type: 'swimlane.create', swimlane: { id: swimlaneId, boardId, title: levelTitle(depth) }, beforeId: null },
      { type: 'row.create', row: { id: rowId, boardId, swimlaneId, title: GENERATED_ROW_TITLE }, beforeId: null },
    )
    inverse.unshift({ type: 'swimlane.delete', id: swimlaneId })
    resolver.remember('swimlanes', { id: swimlaneId, title: levelTitle(depth) })
    resolver.remember('rows', { id: rowId, title: GENERATED_ROW_TITLE, swimlaneId })
    rowByDepth.set(depth, rowId)
  }

  // --- cards: their operations follow anything placements make, so they are kept apart ---
  const cardForward: Operation[] = []
  const cardInverse: Operation[] = []
  const order = new CellOrder(state)
  const cardIdByNode = new Map<string, string>()
  const created = new Map<string, Card>()
  for (const generated of plan.cards) {
    const existing = cards.get(generated.node.id)
    const place = () =>
      placeCard(resolver, generated.node.placement, {
        columnId: () => columnIdByNode.get(generated.columnNodeId),
        rowId: () => rowByDepth.get(generated.depth),
      })
    if (existing) {
      cardIdByNode.set(generated.node.id, existing.id)
      const fields: CardPatch = { ...nodeCardFields(generated.node), colour: nearestCardColour(generated.colour) }
      const changes = updateMatchedCard(order, existing, fields, generated.node.placement, place)
      if (changes.forward.length > 0) {
        cardForward.push(...changes.forward)
        cardInverse.unshift(...changes.inverse)
        summary.updated += 1
      }
      continue
    }
    const { rowId, columnId } = place()
    if (!rowId || !columnId) continue
    const card = newCard(boardId, generated, rowId, columnId, now)
    created.set(generated.node.id, card)
    cardIdByNode.set(generated.node.id, card.id)
    summary.added += 1
  }

  // `contains` links for new children: on the new parent card, or added to an existing one.
  const addedLinks = new Map<string, Relationship[]>()
  for (const generated of plan.cards) {
    if (!generated.parentNodeId || !created.has(generated.node.id)) continue
    const childId = cardIdByNode.get(generated.node.id)!
    const parentNew = created.get(generated.parentNodeId)
    if (parentNew) {
      parentNew.relationships = [...(parentNew.relationships ?? []), newRelationship(childId, 'contains')]
      continue
    }
    const parentId = cardIdByNode.get(generated.parentNodeId)
    if (parentId) addedLinks.set(parentId, [...(addedLinks.get(parentId) ?? []), newRelationship(childId, 'contains')])
  }
  // A new card takes its node's relationships, now every card has an id.
  for (const generated of plan.cards) {
    const card = created.get(generated.node.id)
    if (card) card.relationships = [...(card.relationships ?? []), ...cardRelationships(generated.node, cardIdByNode)].slice(0, CONTEXT_LIMITS.relationshipCount)
  }
  for (const card of created.values()) {
    const { position: _position, updatedAt: _updatedAt, ...input } = card
    cardForward.push({ type: 'card.create', card: input, beforeId: null })
    cardInverse.unshift({ type: 'card.delete', id: card.id })
  }
  // An existing card gains what it lacks — new `contains` links, its node's relationships and
  // references — after the new cards it may point at exist.
  for (const generated of plan.cards) {
    const existing = cards.get(generated.node.id)
    const links = existing ? linksToAdd(existing, generated.node, cardIdByNode, addedLinks.get(existing.id)) : null
    if (!existing || !links) continue
    cardForward.push({ type: 'card.update', id: existing.id, patch: links.patch })
    cardInverse.unshift({ type: 'card.update', id: existing.id, patch: links.before })
  }

  // --- what lost its node ---
  const generatedFrom = [...Object.values(state.groups), ...Object.values(state.columns), ...Object.values(state.cards)]
  for (const entity of generatedFrom) if (entity.sourceNodeId && !live.has(entity.sourceNodeId)) summary.sourceRemoved += 1

  const placed = resolver.operations(boardId)
  forward.push(...placed.forward, ...cardForward)
  const undo = [...cardInverse, ...placed.inverse, ...inverse]
  if (forward.length === 0) return { result: null, summary }
  return { result: { label: 'Update from mindmap', forward, inverse: undo }, summary }
}

// --- keeping labels and colours in sync across the link ---------------------------

const SYNC_BOTH: LinkSync = { labels: true, colours: true }

/** Each node's text and the card colour it is drawn as (its own, or inherited). */
function nodeFaces(root: MindmapNode): Map<string, { text: string; colour: CardColour }> {
  const faces = new Map<string, { text: string; colour: CardColour }>()
  const walk = (node: MindmapNode, inherited: string) => {
    const colour = node.colour ?? inherited
    faces.set(node.id, { text: node.title, colour: nearestCardColour(colour) })
    for (const child of node.children) walk(child, colour)
  }
  walk(root, root.colour ?? MINDMAP_ROOT_COLOUR)
  return faces
}

/**
 * The board operations that carry one mindmap save's label and colour changes to a board
 * generated from it. Only items whose node changed in this save are touched, so a hand
 * edit on the board stands until its node changes again.
 */
export function mindmapSyncOperations(state: NormalizedBoard, before: MindmapNode, after: MindmapNode, sync: LinkSync = SYNC_BOTH): Operation[] {
  if (!sync.labels && !sync.colours) return []
  const was = nodeFaces(before)
  const now = nodeFaces(after)
  const change = (nodeId: string | undefined) => {
    const old = nodeId ? was.get(nodeId) : undefined
    const next = nodeId ? now.get(nodeId) : undefined
    if (!old || !next) return null
    return {
      text: sync.labels && old.text !== next.text ? next.text : null,
      colour: sync.colours && old.colour !== next.colour ? next.colour : null,
    }
  }
  const operations: Operation[] = []
  for (const group of Object.values(state.groups)) {
    const text = change(group.sourceNodeId)?.text
    const title = text == null ? null : titleAndOverflow(text, LIMITS.title).title
    if (title !== null && title !== group.title) operations.push({ type: 'group.rename', id: group.id, title })
  }
  for (const column of Object.values(state.columns)) {
    const text = change(column.sourceNodeId)?.text
    const title = text == null ? null : titleAndOverflow(text, LIMITS.title).title
    if (title !== null && title !== column.title) operations.push({ type: 'column.rename', id: column.id, title })
  }
  for (const card of Object.values(state.cards)) {
    const changed = change(card.sourceNodeId)
    if (!changed) continue
    const patch: CardPatch = {}
    const title = changed.text == null ? null : titleAndOverflow(changed.text, LIMITS.cardTitle).title
    if (title !== null && title !== card.title) patch.title = title
    if (changed.colour !== null && changed.colour !== card.colour) patch.colour = changed.colour
    if (Object.keys(patch).length > 0) operations.push({ type: 'card.update', id: card.id, patch })
  }
  return operations
}

/**
 * The mindmap tree after a board batch's label and colour changes are carried back to the
 * nodes the board was generated from, or null when no node changes. `state` is the board
 * after the batch. A card's colour becomes its node's own colour; Default clears it.
 */
export function boardSyncToMindmap(root: MindmapNode, state: NormalizedBoard, operations: readonly Operation[], sync: LinkSync = SYNC_BOTH): MindmapNode | null {
  const edits = new Map<string, { text?: string; colour?: string | null }>()
  const edit = (nodeId: string | undefined, change: { text?: string; colour?: string | null }) => {
    if (nodeId) edits.set(nodeId, { ...edits.get(nodeId), ...change })
  }
  for (const operation of operations) {
    if (operation.type === 'group.rename' && sync.labels) edit(state.groups[operation.id]?.sourceNodeId, { text: operation.title })
    if (operation.type === 'column.rename' && sync.labels) edit(state.columns[operation.id]?.sourceNodeId, { text: operation.title })
    if (operation.type === 'card.update') {
      const card = state.cards[operation.id]
      if (sync.labels && operation.patch.title !== undefined) edit(card?.sourceNodeId, { text: operation.patch.title })
      if (sync.colours && operation.patch.colour !== undefined) {
        const colour = operation.patch.colour
        edit(card?.sourceNodeId, { colour: colour === 'default' ? null : CARD_COLOUR_HEX[colour] })
      }
    }
  }
  if (edits.size === 0) return null
  const next = structuredClone(root)
  let changed = false
  const walk = (node: MindmapNode) => {
    const change = edits.get(node.id)
    // A long node text shows truncated on the board; an unchanged title keeps the full text.
    const shown = (max: number) => titleAndOverflow(node.title, max).title
    if (change?.text !== undefined && change.text !== node.title && change.text !== shown(LIMITS.title) && change.text !== shown(LIMITS.cardTitle)) {
      node.title = change.text
      changed = true
    }
    if (change?.colour !== undefined && change.colour !== node.colour) {
      node.colour = change.colour
      changed = true
    }
    node.children.forEach(walk)
  }
  walk(next)
  return changed ? next : null
}
